import { apiUrl } from "../../utils/url";
import { HttpClient } from "../HttpClient";
import { getErrorMessage } from "../errors";
import type {
  AttendeeFileEntry,
  AttendeeFileMetadata,
  AttendeeFilesPageResponse,
  AttendeeFilesPageable,
  IAttendeeFilesService,
} from "./IAttendeeFilesService";

export class AttendeeFilesService implements IAttendeeFilesService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient({
      baseURL: apiUrl,
      timeout: 30000,
      headers: {},
    });
  }

  async getAttendeeFiles(
    attendeeId: number,
    pageable: AttendeeFilesPageable,
    typeId: number,
    includePresignedURL?: boolean,
  ): Promise<AttendeeFilesPageResponse> {
    try {
      const params = new URLSearchParams({
        page: String(pageable.page),
        size: String(pageable.size),
      });

      if (pageable.sort) {
        params.append("sort", pageable.sort);
      }
      params.append("typeId", String(typeId));
      if (typeof includePresignedURL === "boolean") {
        params.append("includePresignedURL", String(includePresignedURL));
      }

      const raw = await this.httpClient.get<any>(
        `/attendeeFiles/attendee/${attendeeId}?${params.toString()}`,
      );
      return normalizeAttendeeFilesPageResponse(raw);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar os ficheiros do animal. Atualize a página e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async uploadAttendeeFile(
    metadata: AttendeeFileMetadata,
    file: File | Blob,
  ): Promise<number> {
    try {
      const formData = new FormData();
      formData.append(
        "metadata",
        new Blob([JSON.stringify(metadata)], { type: "application/json" }),
      );
      formData.append("file", file);

      return await this.httpClient.post<number>(
        "/attendeeFiles",
        formData,
        {
          headers: {
            Accept: "application/json",
          },
        },
        true,
      );
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar o ficheiro do animal. Confirme o tipo e tamanho do ficheiro.",
      );
      throw new Error(errorMessage);
    }
  }
}

const normalizeAttendeeFilesPageResponse = (
  raw: any,
): AttendeeFilesPageResponse => {
  if (Array.isArray(raw)) {
    return {
      content: raw.map(mapBackendAttendeeFileEntry),
      page: {
        size: raw.length,
        number: 0,
        totalElements: raw.length,
        totalPages: raw.length > 0 ? 1 : 0,
      },
    };
  }

  const content = Array.isArray(raw?.content)
    ? raw.content.map(mapBackendAttendeeFileEntry)
    : [];
  return {
    content,
    page: {
      size: Number(raw?.page?.size ?? content.length ?? 0),
      number: Number(raw?.page?.number ?? 0),
      totalElements: Number(raw?.page?.totalElements ?? content.length ?? 0),
      totalPages: Number(raw?.page?.totalPages ?? (content.length > 0 ? 1 : 0)),
    },
  };
};

type BackendAttendeeFileEntry = {
  id: number;
  file: {
    id: number;
    uploadedOn: string;
    type: { id: number; value: string };
    name: string;
    extension: string;
    description?: string | null;
    presignedUrl: string;
  };
};

const mapBackendAttendeeFileEntry = (
  raw: BackendAttendeeFileEntry,
): AttendeeFileEntry => {
  return {
    id: raw.id,
    file: {
      id: raw.file.id,
      uploadedOn: raw.file.uploadedOn,
      type: raw.file.type,
      name: raw.file.name,
      extension: raw.file.extension,
      description: raw.file.description ?? undefined,
      presignedUrl: raw.file.presignedUrl,
    },
  };
};
